import { Colors, Spacing, StackChildren, Text } from '@doordash/design-language'
import { get } from 'lodash'
import * as React from 'react'
import { connect } from 'react-redux'
import { Action } from 'redux'
import { VehicleType } from '../../../../constants'
import * as events from '../../../../events'
import { IDVStatusResponse } from '../../../../models/types'
import * as DA from '../../../../typeModules/events'
import { IDVStatus } from '../../../../utilities/steps'
import ProgressionListItem from '../../../molecules/progression-list-item'
import { CompleteDeliveryStepProps } from '../CompleteDeliveryStep'
import { ConfirmActivationStepProps } from '../ConfirmActivationStep'
import { OwnProps as ScheduleOrientationOwnProps } from '../ScheduleOrientationStep'
import { VerifyIdentityStepProps } from '../VerifyIdentityStep'
import strings from '../strings'
import {
  underReviewAndHardFailIdvStatuses,
  progressionCheckStepNames,
} from './constants'
import { getActiveStep } from './utils/get-active-step'
import { progressionCheckSteps, ProgressionCheckStep } from './config'

type StepProps = CompleteDeliveryStepProps &
  ConfirmActivationStepProps &
  ScheduleOrientationOwnProps &
  VerifyIdentityStepProps

type OwnProps = {
  isOrientationEnabled: boolean
}

type StateProps = {
  uniqueLink?: string
  idvStatus?: IDVStatusResponse
  identityVerificationStatus: IDVStatus
  vehicleType: VehicleType
  identityInquiryId: string
  currentStep: string
  hasCompletedFirstDelivery: boolean
  steps: ProgressionCheckStep[]
}

type DispatchProps = {
  trackProgressionCheckRendered?: (
    action: DA.EventActions,
    step: string,
    applicantId: string,
    clientDriven: boolean
  ) => Action
}

type ProgressionCheckProps = OwnProps & StateProps & DispatchProps

const ProgressionCheck: React.FC<ProgressionCheckProps> = ({
  uniqueLink,
  trackProgressionCheckRendered,
  steps,
  currentStep,
  identityVerificationStatus,
  identityInquiryId,
  vehicleType,
  idvStatus,
  hasCompletedFirstDelivery,
  isOrientationEnabled,
}) => {
  React.useEffect(() => {
    trackProgressionCheckRendered(
      DA.EventActions.Rendered,
      currentStep,
      uniqueLink,
      true
    )
  }, [trackProgressionCheckRendered, currentStep, uniqueLink])

  const currentStepIndex = steps.findIndex(step => step.id === currentStep)

  return (
    <StackChildren size={Spacing.Large}>
      <Text color={Colors.TextPrimary} styles={Text.Styles.MajorPageTitle}>
        {strings.progressionCheckTitle()}
      </Text>
      <div>
        {steps.map((progressionCheckStep: ProgressionCheckStep, stepIndex) => {
          const {
            shouldRenderChildrenComponent,
            component: stepComponent,
            getTitle,
            getIsCompleted,
          } = progressionCheckStep
          const displayIndex = stepIndex + 1
          const isCurrentStep = stepIndex === currentStepIndex
          let isStepCompleted = stepIndex < currentStepIndex
          let isActive = isCurrentStep

          if (getIsCompleted) {
            isStepCompleted = getIsCompleted({ hasCompletedFirstDelivery })
            isActive = isStepCompleted ? false : isActive
          }

          const stepData: StepProps = {
            uniqueLink,
            vehicleType,
            idvStatus: idvStatus.value,
            identityVerificationStatus,
            identityInquiryId,
            isStepCompleted,
            isOrientationEnabled,
          }

          const identityVericationInProgress = !underReviewAndHardFailIdvStatuses.includes(
            identityVerificationStatus
          )

          const title = getTitle({
            identityVericationInProgress,
            identityInquiryId,
            isCompleted: isStepCompleted,
          })

          const shouldRenderChildren =
            isCurrentStep ||
            (isStepCompleted &&
              shouldRenderChildrenComponent &&
              shouldRenderChildrenComponent({ currentStep }))

          return (
            <ProgressionListItem
              isActive={isActive}
              title={title}
              isComplete={isStepCompleted}
              key={stepIndex}
              step={displayIndex}
              shouldRenderChildren={shouldRenderChildren}
            >
              {/*
                IMPROVE @paipo-dd: Currently, it's too implicit to pass the data to the `stepComponent`.
                Also `stepComponent` can have be different components with totally different props interface,
                so with current way (using React.cloneElement to pass props), it's impossible for the compiler to know how to do proper type check.
                Think about doing a refactor of `ProgressionCheck` component soon.
              */}
              {stepComponent && React.cloneElement(stepComponent, stepData)}
            </ProgressionListItem>
          )
        })}
      </div>
    </StackChildren>
  )
}

const removeOrientationStep = step =>
  step.id !== progressionCheckStepNames.SCHEDULE_ORIENTATION_STEP

const mapStateToProps = (state, ownProps: OwnProps): ProgressionCheckProps => {
  const {
    application,
    scheduledOrientation,
    idvStatus,
    applicationStatus,
  } = state
  const { isOrientationEnabled } = ownProps

  const hasCompletedFirstDelivery = get(
    applicationStatus,
    'data.onboardingProgress.hasCompletedFirstDelivery'
  )

  const progressionCheckStepsWithoutOrientation = progressionCheckSteps.filter(
    removeOrientationStep
  )
  const steps = isOrientationEnabled
    ? progressionCheckSteps
    : progressionCheckStepsWithoutOrientation
  const identityVerificationStatus = get(
    application,
    'identityVerificationStatus',
    IDVStatus.NOT_COMPLETED
  )
  const identityInquiryId = get(application, 'identityInquiryId')
  const vehicleType = get(application, 'vehicleType', VehicleType.Car)
  const backgroundCheck = get(application, 'backgroundCheckResults')
  const uniqueLink = get(application, 'uniqueLink')
  const currentStep = getActiveStep(
    isOrientationEnabled,
    scheduledOrientation,
    idvStatus.value,
    identityVerificationStatus,
    backgroundCheck
  )
  return {
    steps,
    identityInquiryId,
    vehicleType,
    currentStep,
    identityVerificationStatus,
    idvStatus,
    uniqueLink,
    hasCompletedFirstDelivery,
    isOrientationEnabled,
  }
}

const mapDispatchToProps = {
  trackProgressionCheckRendered: events.trackProgressionCheckRendered,
}

export default connect(mapStateToProps, mapDispatchToProps)(ProgressionCheck)
